import { useQueryClient } from '@tanstack/react-query'
import { createContext, use, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { io, type Socket } from 'socket.io-client'

/**
 * Canal en vivo.
 *
 * El socket funciona como **bus de invalidación**, no como fuente de datos: los
 * eventos dicen «esto cambió» y React Query vuelve a pedir lo afectado por
 * HTTP. Es una decisión deliberada, y coincide con cómo está diseñado el
 * servidor.
 *
 * Parchear la caché con la carga del evento sería más rápido, pero traería dos
 * problemas serios en una app de dinero: la carga es parcial —lleva lo justo
 * para avisar, no la entidad completa— y sobre todo, los permisos por rol están
 * resueltos en las consultas HTTP. Recalcular en el cliente qué puede ver un
 * integrante limitado sería duplicar esa lógica en el sitio donde menos se
 * puede confiar en ella.
 *
 * La autenticación va en la cookie de sesión, que viaja sola por ser el mismo
 * origen. No se manda ningún token por el socket.
 */

interface RealtimeValue {
  conectado: boolean
  /** Quién está capturando un gasto ahora mismo, para la señal de presencia. */
  componiendo: string[]
  anunciarCaptura: (activo: boolean) => void
}

const RealtimeContext = createContext<RealtimeValue>({
  conectado: false,
  componiendo: [],
  anunciarCaptura: () => undefined,
})

export function useRealtime(): RealtimeValue {
  return use(RealtimeContext)
}

/**
 * Qué recargar ante cada evento.
 *
 * Un movimiento nuevo toca cuatro sitios a la vez: el disponible del tablero,
 * la lista, el saldo de la cuenta y —si fue a tarjeta— la tarjeta. Tenerlo en
 * una tabla evita que al añadir un evento se olvide invalidar alguno y la app
 * quede mostrando cifras viejas.
 */
const AFECTA: Record<string, string[][]> = {
  'transaction:created': [['dashboard'], ['transactions'], ['accounts'], ['cards']],
  'transaction:updated': [['dashboard'], ['transactions'], ['accounts'], ['cards']],
  'transaction:deleted': [['dashboard'], ['transactions'], ['accounts'], ['cards']],
  'card:statement-updated': [['dashboard'], ['cards']],
  // Una compra a meses toca tres sitios: su propia lista, la tarjeta —la
  // mensualidad entra en el corte— y el disponible, que la descuenta como
  // comprometida desde que el plan existe.
  'installment:created': [['installments'], ['cards'], ['dashboard']],
  'installment:updated': [['installments'], ['cards'], ['dashboard']],
  'installment:closed': [['installments'], ['cards'], ['dashboard']],
  'card:paid': [['dashboard'], ['cards'], ['accounts'], ['transactions']],
  'goal:progress': [['dashboard'], ['savings']],
  'income:confirmed': [['dashboard']],
  'savings:decided': [['dashboard'], ['savings']],
  'member:joined': [['household'], ['dashboard'], ['session']],
  'account:updated': [['dashboard'], ['accounts']],
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [conectado, setConectado] = useState(false)
  const [componiendo, setComponiendo] = useState<string[]>([])
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    const socket = io('/household', {
      withCredentials: true,
      // Se dejan los dos transportes en el orden por defecto: arranca por
      // sondeo y sube a WebSocket en cuanto puede. Forzar WebSocket deja la app
      // sin canal si el upgrade falla —como ocurre tras el proxy de desarrollo—
      // en vez de degradar a algo que sí funciona.
      transports: ['polling', 'websocket'],
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10_000,
    })
    socketRef.current = socket

    socket.on('realtime:ready', () => setConectado(true))

    socket.on('realtime:denied', () => {
      // Sesión inválida: se corta sin reintentar. Reconectar en bucle contra un
      // servidor que ya dijo que no solo gastaría batería.
      setConectado(false)
      socket.disconnect()
    })

    socket.on('disconnect', () => setConectado(false))

    socket.on('connect', () => {
      // Tras una reconexión pudo cambiar cualquier cosa mientras no había
      // canal, así que se recarga todo en vez de confiar en lo que hay en
      // memoria.
      void queryClient.invalidateQueries()
    })

    for (const [evento, claves] of Object.entries(AFECTA)) {
      socket.on(evento, () => {
        for (const clave of claves) {
          void queryClient.invalidateQueries({ queryKey: clave })
        }
      })
    }

    socket.on('presence:composing', (payload: { displayName?: string }) => {
      const nombre = payload?.displayName
      if (nombre) setComponiendo((prev) => (prev.includes(nombre) ? prev : [...prev, nombre]))
    })

    socket.on('presence:idle', (payload: { displayName?: string }) => {
      const nombre = payload?.displayName
      if (nombre) setComponiendo((prev) => prev.filter((n) => n !== nombre))
    })

    return () => {
      socket.removeAllListeners()
      socket.disconnect()
      socketRef.current = null
    }
  }, [queryClient])

  const value = useMemo<RealtimeValue>(
    () => ({
      conectado,
      componiendo,
      anunciarCaptura: (activo: boolean) => {
        socketRef.current?.emit(activo ? 'presence:composing' : 'presence:idle')
      },
    }),
    [conectado, componiendo],
  )

  return <RealtimeContext value={value}>{children}</RealtimeContext>
}

/**
 * Aviso de que la otra persona está registrando algo.
 *
 * Sirve para algo concreto en una app de pareja: evita que ambos capturen la
 * misma compra del súper al salir de la tienda.
 */
export function PresenciaAviso() {
  const { componiendo } = useRealtime()
  if (componiendo.length === 0) return null

  const nombres = componiendo.map((n) => n.split(' ')[0]).join(' y ')
  return (
    <p className="px-safe mx-auto max-w-lg pb-2 text-[12px] text-ink-soft" aria-live="polite">
      {nombres} {componiendo.length === 1 ? 'está registrando' : 'están registrando'} un gasto…
    </p>
  )
}
